import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { normalizeRole, roleLabelFor } from '../permissions';
import { ipApi, isLiveApi, setStaffToken } from '../services/api';
import { PORTALS } from '../config/systems';
import { isCognitoEnabled, cognitoLogin } from './cognito';
import { hasValidStaffSession, clearStaffSession, STORAGE_KEY } from './session';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    if (!hasValidStaffSession()) {
      clearStaffSession();
      return null;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      clearStaffSession();
      return null;
    }
  });

  const login = useCallback(async (email, password) => {
    if (!isLiveApi()) {
      throw new Error('Live API required. Configure REACT_APP_IP_API_URL and rebuild.');
    }
    clearStaffSession();
    const trimmed = email.toLowerCase().trim();
    let data;
    if (isCognitoEnabled()) {
      data = await cognitoLogin(trimmed, password);
    } else {
      data = await ipApi.login(trimmed, password);
    }
    const role = normalizeRole(data.user.role);
    const session = {
      email: data.user.email,
      name: data.user.name || data.user.email,
      role,
      roleLabel: roleLabelFor(role),
      hospital: data.user.hospitalName || data.user.hospital || data.hospitalProfile?.name || 'Hospital',
      hospitalId: data.user.hospitalId || '',
      staffId: data.user.staffId,
      portal: PORTALS.NETWORKS.id,
      isOwner: !!data.user.isHospitalOwner,
      hospitalProfile: data.hospitalProfile || null,
      specialty: data.user.specialty || '',
      department: data.user.department || '',
      enableDentalOp: !!data.user.enableDentalOp,
      regNo: data.user.regNo || '',
      rxPrintTemplate: data.user.rxPrintTemplate || null,
    };
    setStaffToken(data.token);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setUser(session);
    return session;
  }, []);

  const logout = useCallback(() => {
    clearStaffSession();
    setUser(null);
  }, []);

  const patchUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, patchUser, isLoggedIn: !!user, portal: PORTALS.NETWORKS }),
    [user, login, logout, patchUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
